import { Box, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import BannerSelect from "./BannerSelect";
import RetailerSelect from "./RetailerSelect";
import ProvinceSelect from "./ProvinceSelect";
import SyndicationSelect from "./SyndicationSelect";
import SyndicatedTerritoriesSelect from "./SyndicatedTerritoriesSelect";
import LocationStatusSelect from "./LocationStatusSelect";
import Input from "../elements/Input";
import Select from "../elements/Select";
import Switch from "../elements/Switch";

import ToolbarItem from "../layout/ToolbarItem";

import useLocalization from "../../hooks/useLocalization";

const Filter = ({
  fields,
  fieldRows,
  filter,
  onFilterChange,
  onSearch,
  onClear,
  roleItems = [],
  activeStatusValue = "",
  activeStatusItems = [],
  onActiveStatusChange,
}) => {
  const theme = useTheme();
  const { getLabel } = useLocalization();

  const updateFilterValue = (name, value) => {
    let newFilter = {};

    if (filter) {
      const keys = Object.keys(filter);
      for (const key of keys) {
        if (name === key) {
          if (value) {
            newFilter[key] = value;
          }
        } else {
          newFilter[key] = filter[key];
        }
      }
      if (!keys.includes(name)) {
        newFilter[name] = value;
      }
    } else {
      newFilter = { [name]: value };
    }

    // console.log({ newFilter });

    onFilterChange(newFilter);
  };

  const handleClearFilter = () => {
    onFilterChange(null);
    onClear();
  };

  const renderFilterField = (fieldKey) => {
    switch (fieldKey) {
      case "retailer":
        return (
          <RetailerSelect
            value={filter?.retailerId || ""}
            onChange={(newValue) => updateFilterValue("retailerId", newValue)}
          />
        );
      case "banner":
        return (
          <BannerSelect
            value={filter?.bannerId || ""}
            onChange={(newValue) => updateFilterValue("bannerId", newValue)}
          />
        );
      case "locationId":
        return (
          <Input
            label="Location ID"
            value={filter?.locationId || ""}
            onChange={(newValue) => updateFilterValue("locationId", newValue)}
          />
        );
      case "locationNumber":
        return (
          <Input
            label="Location Number"
            value={filter?.locationNumber || ""}
            onChange={(newValue) =>
              updateFilterValue("locationNumber", newValue)
            }
          />
        );
      case "city":
        return (
          <Input
            label={getLabel("city")}
            value={filter?.["address.city"] || ""}
            onChange={(newValue) => updateFilterValue("address.city", newValue)}
          />
        );
      case "province":
        return (
          <ProvinceSelect
            value={filter?.["address.province"] || ""}
            onChange={(newValue) =>
              updateFilterValue("address.province", newValue)
            }
          />
        );
      case "syndication":
        return (
          <SyndicationSelect
            value={filter?.syndicationId || ""}
            onChange={(newValue) =>
              updateFilterValue("syndicationId", newValue)
            }
          />
        );
      case "syndicatedTerritories":
        return (
          <SyndicatedTerritoriesSelect
            value={filter?.syndicatedTerritoryIds || []}
            onChange={(newValue) =>
              updateFilterValue("syndicatedTerritoryIds", newValue)
            }
            filter={filter}
          />
        );
      case "locationStatus":
        return (
          <LocationStatusSelect
            value={filter?.status || ""}
            onChange={(newValue) => updateFilterValue("status", newValue)}
          />
        );
      case "search":
        return (
          <Input
            label={getLabel("search")}
            value={filter?.$text?.$search || ""}
            onChange={(newValue) =>
              updateFilterValue("$text", { $search: newValue })
            }
          />
        );
      case "role":
        return (
          <Select
            label="role"
            value={filter?.role || "all"}
            items={roleItems}
            includeNone
            noneLabel={getLabel("all") || "All"}
            onChange={(newValue) =>
              updateFilterValue("role", newValue === "all" ? null : newValue)
            }
          />
        );
      case "activeStatus":
        return (
          <Select
            label="status"
            value={activeStatusValue}
            items={activeStatusItems}
            onChange={(newValue) => {
              if (typeof onActiveStatusChange === "function") {
                onActiveStatusChange(newValue);
              }
            }}
          />
        );
      case "includeInactiveRetailers":
        return (
          <Switch
            label="Include inactive retailers"
            value={filter?.includeInactive === true}
            onChange={(newValue) =>
              updateFilterValue("includeInactive", Boolean(newValue))
            }
          />
        );
      default:
        return null;
    }
  };

  const normalizedRows =
    Array.isArray(fieldRows) && fieldRows.length > 0
      ? fieldRows
      : [Array.isArray(fields) ? fields : []];

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      gap={1}
    >
      <Box
        display="flex"
        flexDirection="column"
        gap={1}
        minWidth={0}
      >
        {normalizedRows.map((row, rowIndex) => (
          <Box
            key={`filter-row-${rowIndex}`}
            display="flex"
            gap={1}
            flexWrap="wrap"
          >
            {(row || []).map((fieldKey) => (
              <Box key={`${rowIndex}-${fieldKey}`}>
                {renderFilterField(fieldKey)}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box
        display="flex"
        backgroundColor={theme.palette.primary.main}
        padding="5px"
        borderRadius="5px"
        gap={1}
      >
        <ToolbarItem
          name="search"
          iconName="Search"
          onClick={onSearch}
        />
        <Divider orientation="vertical" />
        <ToolbarItem
          name="clear"
          iconName="Cancel"
          onClick={handleClearFilter}
        />
      </Box>
    </Box>
  );
};

export default Filter;
